/** Curated public fiction assets. General member photos continue through private media. */
export const SAMPLE_MEDIA_ROOT = 'https://samples.garagegymbros.com/';
const UUID = /^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i;
const FILE = /^[a-z0-9][a-z0-9_-]{0,79}\.(?:webp|png|jpe?g)$/;
export interface SampleMemberMedia {
  version: 1;
  avatar_url: string;
  gym_cover_url: string;
  gym_name?: string;
  gallery: Array<{ image: string; caption: string }>;
  post_images: Record<string, string>;
  story_images: Record<string, string>;
}
/** Hide importer-only labels in the display copy of validated member content. */
export function sampleMemberDisplayText(text: string, media?: SampleMemberMedia): string {
  if (!media) return text;
  return text
    .replace(/^\[Fictional sample\]\s*/, '')
    .replace(
      /(?:^|\s+)(?:Fictional sample member and illustrative gym; no real ownership is claimed\.|Fictional sample member\.)\s*$/,
      '',
    );
}
export function sampleImageSource(value: unknown, ownerId?: string): string | null {
  if (typeof value !== 'string' || !value.startsWith(SAMPLE_MEDIA_ROOT)) return null;
  const parts = value.slice(SAMPLE_MEDIA_ROOT.length).split('/');
  return parts.length === 2 &&
    UUID.test(parts[0]) &&
    FILE.test(parts[1]) &&
    (!ownerId || parts[0] === ownerId)
    ? value
    : null;
}
function imageMap(value: unknown, ownerId: string): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value);
  if (entries.length > 10) return null;
  const valid: Record<string, string> = {};
  for (const [id, image] of entries) {
    const source = sampleImageSource(image, ownerId);
    if (!UUID.test(id) || !source) return null;
    valid[id] = source;
  }
  return valid;
}
export function sampleMemberMediaFromRow(row: {
  id?: unknown;
  is_demo?: unknown;
  sample_media?: unknown;
  avatar_media_id?: unknown;
  bio?: unknown;
}): SampleMemberMedia | undefined {
  if (row.is_demo !== true || typeof row.id !== 'string' || !UUID.test(row.id)) return;
  const raw = row.sample_media;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
  if (JSON.stringify(raw).length > 16_384) return;
  const value = raw as Record<string, unknown>;
  if (value.version !== 1) return;
  const avatar = sampleImageSource(value.avatar_url, row.id);
  const cover = sampleImageSource(value.gym_cover_url, row.id);
  if (
    !avatar ||
    !cover ||
    !Array.isArray(value.gallery) ||
    value.gallery.length < 1 ||
    value.gallery.length > 6
  )
    return;
  const gallery: SampleMemberMedia['gallery'] = [];
  for (const entry of value.gallery) {
    if (!entry || typeof entry !== 'object') return;
    const image = sampleImageSource(entry.image, row.id);
    if (
      !image ||
      typeof entry.caption !== 'string' ||
      !entry.caption.trim() ||
      entry.caption.length > 140
    )
      return;
    gallery.push({ image, caption: entry.caption });
  }
  const postImages = imageMap(value.post_images, row.id);
  const storyImages = imageMap(value.story_images, row.id);
  if (!postImages || !storyImages) return;
  const gymName =
    typeof value.gym_name === 'string' ? value.gym_name.trim().slice(0, 100) : undefined;
  return {
    version: 1,
    avatar_url: avatar,
    gym_cover_url: cover,
    gym_name: gymName,
    gallery,
    post_images: postImages,
    story_images: storyImages,
  };
}
export function sampleMediaMemberFields(row: Parameters<typeof sampleMemberMediaFromRow>[0]): {
  sample_media?: SampleMemberMedia;
  avatar_url?: string;
  bio?: string;
} {
  const media = sampleMemberMediaFromRow(row);
  return media
    ? {
        sample_media: media,
        avatar_url: typeof row.avatar_media_id === 'string' ? undefined : media.avatar_url,
        ...(typeof row.bio === 'string' ? { bio: sampleMemberDisplayText(row.bio, media) } : {}),
      }
    : {};
}
